import SalesCashHandoverReceiptsApi from 'api/admins/sales-cash-handover-receipts-api'
import SalesUsersApi from 'api/admins/sales-users-api'
import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import { TitlePage } from 'components/atoms'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import { useHistory, useLocation } from 'react-router'
import { Alert, Button, Card, CardBody, Col, Container, Form, Input, Label, Row, Spinner, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const PER_PAGE_OPTIONS = [10, 25, 50]
const PAGE_TITLE = 'Tanda Terima General'
const ENTRY_MODE_MANUAL = 'manual'
const ENTRY_MODE_DATABASE = 'database'
const NO_CURRENCY_VALUE = 'none'
const MIXED_CURRENCY_VALUE = 'mixed'
const CURRENCY_CODES = [
  'USD', 'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG',
  'AZN', 'BAM', 'BBD', 'BDT', 'BGN', 'BIF', 'BMD', 'BND', 'BOB', 'BRL',
  'BSD', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CLP', 'CNY', 'COP',
  'CRC', 'CVE', 'CZK', 'DJF', 'DKK', 'DOP', 'DZD', 'EGP', 'ETB', 'EUR',
  'FJD', 'FKP', 'GBP', 'GEL', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HKD',
  'HNL', 'HRK', 'HTG', 'HUF', 'IDR', 'ILS', 'INR', 'ISK', 'JMD', 'JPY',
  'KES', 'KGS', 'KHR', 'KMF', 'KRW', 'KYD', 'KZT', 'LAK', 'LBP', 'LKR',
  'LRD', 'LSL', 'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRO',
  'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN', 'NAD', 'NGN', 'NIO', 'NOK',
  'NPR', 'NZD', 'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG', 'QAR',
  'RON', 'RSD', 'RUB', 'RWF', 'SAR', 'SBD', 'SCR', 'SEK', 'SGD', 'SHP',
  'SLL', 'SOS', 'SRD', 'STD', 'SZL', 'THB', 'TJS', 'TOP', 'TRY', 'TTD',
  'TWD', 'TZS', 'UAH', 'UGX', 'UYU', 'UZS', 'VND', 'VUV', 'WST', 'XAF',
  'XCD', 'XOF', 'XPF', 'YER', 'ZAR', 'ZMW'
]
const CURRENCY_NAMES = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'Pound Sterling',
  IDR: 'Rupiah',
  SGD: 'Singapore Dollar',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan',
  HKD: 'Hong Kong Dollar',
  JPY: 'Japanese Yen',
  MYR: 'Malaysian Ringgit',
  PHP: 'Philippine Peso',
  THB: 'Thai Baht',
  VND: 'Vietnamese Dong'
}
const SORTED_CURRENCY_CODES = [
  'IDR',
  ...CURRENCY_CODES.filter((code) => code !== 'IDR')
]
const CURRENCY_OPTIONS = [
  { value: NO_CURRENCY_VALUE, label: 'No Currency' },
  ...SORTED_CURRENCY_CODES.map((code) => ({
    value: code.toLowerCase(),
    label: `${CURRENCY_NAMES[code] || code} (${code})`
  }))
]

const filterCurrencyOption = (option, inputValue = '') => {
  const keyword = inputValue.toString().trim().toLowerCase()
  if (!keyword) return true

  const value = (option.value || option.data?.value || '').toString().toLowerCase()
  const label = (option.label || option.data?.label || '').toString().toLowerCase()

  return value.includes(keyword) || label.includes(keyword)
}

const currencySelectProps = {
  filterOption: filterCurrencyOption
}

const noOptionsMessage = ({ inputValue }) => (
  inputValue ? 'No options found.' : 'Type to search.'
)

const asyncSelectProps = {
  cacheOptions     : true,
  className        : 'react-select success',
  classNamePrefix  : 'react-select',
  noOptionsMessage : noOptionsMessage
}

const selectProps = {
  className        : 'react-select success',
  classNamePrefix  : 'react-select',
  noOptionsMessage : noOptionsMessage
}

const selectedCurrencyOption = (currency) => (
  CURRENCY_OPTIONS.find((option) => option.value === currency) || CURRENCY_OPTIONS.find((option) => option.value === NO_CURRENCY_VALUE)
)

const userLabel = (user = {}) => ([
  user.name,
  user.email,
  user.phone
].filter(Boolean).join(' - '))

const userOption = (user = {}) => ({
  value : user.id,
  label : userLabel(user) || user.email || `User #${user.id}`,
  user
})

const inventoryLabel = (inventory = {}) => ([
  inventory.inventory_sku || inventory.product_sku,
  inventory.product_name
].filter(Boolean).join(' - '))

const inventoryOption = (inventory = {}) => ({
  value : inventory.id,
  label : `${inventoryLabel(inventory) || `Inventory #${inventory.id}`} | SQ ${inventory.sellable_quantity ?? '-'} | IQ ${inventory.stored_quantity ?? '-'}`,
  inventory
})

const receiptCurrencyLabel = (currency) => {
  if (currency === MIXED_CURRENCY_VALUE) return 'Mixed Currency'

  return selectedCurrencyOption(currency)?.label || currency?.toString().toUpperCase() || '-'
}

const normalizeCurrencyCode = (currency = 'idr') => (
  currency.toString().trim().toUpperCase() || 'IDR'
)

const moneyCurrency = (currency) => (
  currency && currency !== NO_CURRENCY_VALUE && currency !== MIXED_CURRENCY_VALUE
)

const fallbackCurrencyFormat = (number, currency) => (
  `${normalizeCurrencyCode(currency)} ${number.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
)

const defaultDate = () => new Date().toISOString().slice(0, 10)

const INPUT_MODE_OPTIONS = [
  { value: ENTRY_MODE_DATABASE, label: 'Search From Database' },
  { value: ENTRY_MODE_MANUAL, label: 'Manual' }
]

const newItem = () => ({
  local_key     : `item-${Date.now()}-${Math.random()}`,
  inventory_id  : '',
  product_id    : '',
  inventory_sku : '',
  product_sku   : '',
  product_name  : '',
  description   : '',
  currency      : NO_CURRENCY_VALUE,
  value         : '',
  notes         : ''
})

const initialForm = {
  receipt_date            : defaultDate(),
  currency                : NO_CURRENCY_VALUE,
  entry_mode              : ENTRY_MODE_MANUAL,
  user_id                 : '',
  full_name               : '',
  phone_number            : '',
  email                   : '',
  given_by_name           : '',
  received_by_name        : '',
  second_received_by_name : ''
}

const formatCurrency = (value, currency = 'idr') => {
  if (currency === NO_CURRENCY_VALUE || currency === MIXED_CURRENCY_VALUE) return '-'

  const number = Number(value || 0)

  if (!Number.isFinite(number)) return '-'

  try {
    const normalizedCurrency = normalizeCurrencyCode(currency)

    return new Intl.NumberFormat(normalizedCurrency === 'IDR' ? 'id-ID' : 'en-US', {
      style                 : 'currency',
      currency              : normalizedCurrency,
      maximumFractionDigits : 0
    }).format(number)
  }
  catch (error) {
    return fallbackCurrencyFormat(number, currency || error?.currency)
  }
}

const groupedItemTotals = (items = []) => items.reduce((result, item) => {
  const currency = item.currency || NO_CURRENCY_VALUE

  if (!moneyCurrency(currency)) return result

  return {
    ...result,
    [currency]: Number(result[currency] || 0) + Number(item.value || 0)
  }
}, {})

const receiptCurrencyFromItems = (items = []) => {
  const currencies = Object.keys(groupedItemTotals(items))

  if (!currencies.length) return NO_CURRENCY_VALUE
  if (currencies.length === 1) return currencies[0]

  return MIXED_CURRENCY_VALUE
}

const receiptValueFromItems = (items = []) => {
  const groupedTotals = groupedItemTotals(items)
  const currencies = Object.keys(groupedTotals)

  return currencies.length === 1 ? groupedTotals[currencies[0]] : 0
}

const formatItemsTotal = (items = []) => {
  const groupedTotals = groupedItemTotals(items)
  const currencies = Object.keys(groupedTotals)

  if (!currencies.length) return '-'

  return currencies.map((currency) => formatCurrency(groupedTotals[currency], currency)).join(' / ')
}

const itemHasContent = (item = {}) => (
  item.inventory_id || item.product_id || item.product_name || item.description?.toString().trim()
)

const readFilename = (response, fallback) => {
  const disposition = response?.headers?.['content-disposition']
  if (!disposition) return fallback

  const match = disposition.match(/filename="?([^"]+)"?/i)
  return match?.[1] || fallback
}

const SalesCashHandoverReceiptsListPage = ({ pageUtils }) => {
  const history = useHistory()
  const location = useLocation()
  const editId = new URLSearchParams(location.search).get('edit_id')
  const [ revisionVersion, setRevisionVersion ] = useState(null)
  const [ revisionNote, setRevisionNote ] = useState('')
  const [ loadingRevision, setLoadingRevision ] = useState(false)
  const [ form, setForm ] = useState(initialForm)
  const [ items, setItems ] = useState([ newItem() ])
  const [ receipts, setReceipts ] = useState([])
  const [ meta, setMeta ] = useState({
    current_page : 1,
    per_page     : 10,
    total_pages  : 1,
    total_count  : 0
  })
  const [ q, setQ ] = useState('')
  const [ searchInput, setSearchInput ] = useState('')
  const [ page, setPage ] = useState(1)
  const [ perPage, setPerPage ] = useState(10)
  const [ loading, setLoading ] = useState(false)
  const [ saving, setSaving ] = useState(false)
  const [ downloadingId, setDownloadingId ] = useState(null)
  const [ deletingId, setDeletingId ] = useState(null)
  const [ selectedUser, setSelectedUser ] = useState(null)
  const [ selectedInventory, setSelectedInventory ] = useState(null)

  const totalValue = useMemo(() => receiptValueFromItems(items), [ items ])
  const totalValueLabel = useMemo(() => formatItemsTotal(items), [ items ])
  const receiptCurrency = useMemo(() => receiptCurrencyFromItems(items), [ items ])

  useEffect(() => {
    let active = true
    setRevisionVersion(null)
    setRevisionNote('')
    if (!editId) {
      resetForm()
      setLoadingRevision(false)
      return () => { active = false }
    }
    setLoadingRevision(true)
    SalesCashHandoverReceiptsApi.show({ id: editId }).then(({ data }) => {
      if (!active) return
      const receipt = data.cash_handover_receipt
      if (!receipt?.can_edit) throw new Error('Anda tidak memiliki akses revisi tanda terima ini.')
      const nextForm = Object.keys(initialForm).reduce((values, key) => ({ ...values, [key]: receipt[key] == null ? '' : receipt[key] }), {})
      setForm(nextForm)
      setItems((receipt.items || []).map((item) => ({ ...newItem(), ...item, description: item.description || '', notes: item.notes || '' })))
      setSelectedUser(receipt.user_id ? { value: receipt.user_id, label: receipt.full_name, user: { id: receipt.user_id, name: receipt.full_name, email: receipt.email, phone: receipt.phone_number } } : null)
      setRevisionVersion(receipt.revision_version)
    }).catch((error) => {
      if (active) pageUtils?.setApiErrorMsg?.(error.response?.data || { message: error.message })
    }).finally(() => { if (active) setLoadingRevision(false) })
    return () => { active = false }
  }, [editId]) // eslint-disable-line react-hooks/exhaustive-deps

  const isDatabaseMode = form.entry_mode === ENTRY_MODE_DATABASE
  const showValueColumn = receiptCurrency !== NO_CURRENCY_VALUE
  const canAddManualItem = !isDatabaseMode && items.every(itemHasContent)

  const fetchReceipts = useCallback(async ({ nextPage = page, nextPerPage = perPage, nextQ = q } = {}) => {
    setLoading(true)

    await SalesCashHandoverReceiptsApi.index({
      params: {
        q        : nextQ || undefined,
        page     : nextPage,
        per_page : nextPerPage
      }
    })
      .then((response) => {
        const data = response?.data || {}

        setReceipts(Array.isArray(data.cash_handover_receipts) ? data.cash_handover_receipts : [])
        setMeta(data.meta || {
          current_page : nextPage,
          per_page     : nextPerPage,
          total_pages  : 1,
          total_count  : 0
        })
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to fetch Tanda Terima General receipts.' })
        setReceipts([])
      })
      .finally(() => setLoading(false))
  }, [ page, pageUtils, perPage, q ])

  useEffect(() => {
    fetchReceipts({ nextPage: page, nextPerPage: perPage, nextQ: q })
  }, [ fetchReceipts, page, perPage, q ])

  const updateForm = (key, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [key]: value
    }))
  }

  const handleCurrencyChange = (index, value) => {
    const nextCurrency = value || NO_CURRENCY_VALUE

    setItems((currentItems) => currentItems.map((item, itemIndex) => (
      itemIndex === index ? {
        ...item,
        currency : nextCurrency,
        value    : nextCurrency === NO_CURRENCY_VALUE ? '' : item.value
      } : item
    )))
  }

  const handleEntryModeChange = (value) => {
    setForm((currentForm) => ({
      ...currentForm,
      entry_mode   : value,
      user_id      : '',
      full_name    : '',
      phone_number : '',
      email        : ''
    }))
    setSelectedUser(null)
    setSelectedInventory(null)
    setItems([ newItem() ])
  }

  const loadUserOptions = useCallback((inputValue = '') => {
    const keyword = inputValue.trim()
    if (!keyword) return Promise.resolve([])

    return SalesUsersApi.index({
      params: {
        q        : keyword,
        page     : 1,
        per_page : 10
      }
    })
      .then((response) => (
        (Array.isArray(response?.data?.users) ? response.data.users : []).map(userOption)
      ))
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to search users.' })
        return []
      })
  }, [ pageUtils ])

  const loadInventoryOptions = useCallback((inputValue = '') => {
    const keyword = inputValue.trim()
    if (!keyword) return Promise.resolve([])

    return InventoriesApiV2.search(keyword, {
      per_page                   : 10,
      page                       : 1,
      show_all_inventory         : true,
      eligible_for_cash_handover : true
    })
      .then((response) => (
        (Array.isArray(response?.data?.inventories) ? response.data.inventories : [])
          .filter((inventory) => Number(inventory.stored_quantity ?? inventory.stored_quantity_value ?? inventory.quantity ?? 0) > 0)
          .map(inventoryOption)
      ))
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to search inventory.' })
        return []
      })
  }, [ pageUtils ])

  const updateItem = (index, key, value) => {
    setItems((currentItems) => currentItems.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )))
  }

  const handleAddManualItem = () => {
    setItems((currentItems) => ([ ...currentItems, newItem() ]))
  }

  const handleRemoveItem = (index) => {
    setItems((currentItems) => {
      const nextItems = currentItems.filter((_, itemIndex) => itemIndex !== index)

      return nextItems.length ? nextItems : [ newItem() ]
    })
  }

  const handleSelectUser = (option) => {
    const user = option?.user

    setSelectedUser(option || null)

    if (!user) {
      setForm((currentForm) => ({
        ...currentForm,
        user_id      : '',
        full_name    : '',
        phone_number : '',
        email        : ''
      }))
      return
    }

    setForm((currentForm) => ({
      ...currentForm,
      user_id      : user.id,
      full_name    : user.name || user.email || '',
      phone_number : user.phone || '',
      email        : user.email || ''
    }))
  }

  const handleAddInventoryItem = (inventory) => {
    const alreadyAdded = items.some((item) => item.inventory_id && item.inventory_id === inventory.id)
    if (alreadyAdded) {
      pageUtils?.setAlertMsg?.('Product already added to this receipt.', ALERT_TYPES.WARNING)
      return
    }

    const item = {
      ...newItem(),
      inventory_id  : inventory.id,
      product_id    : inventory.product_id || '',
      inventory_sku : inventory.inventory_sku || '',
      product_sku   : inventory.product_sku || '',
      product_name  : inventory.product_name || '',
      description   : inventoryLabel(inventory),
      value         : '',
      notes         : ''
    }

    setItems((currentItems) => {
      const blankOnly = currentItems.length === 1 &&
        !currentItems[0].description &&
        !currentItems[0].value &&
        !currentItems[0].inventory_id

      return blankOnly ? [ item ] : [ ...currentItems, item ]
    })
  }

  const handleSelectInventory = (option) => {
    setSelectedInventory(option || null)

    if (!option?.inventory) return

    handleAddInventoryItem(option.inventory)
    setSelectedInventory(null)
  }

  const normalizedItems = () => items
    .map((item) => ({
      inventory_id  : item.inventory_id || undefined,
      product_id    : item.product_id || undefined,
      inventory_sku : item.inventory_sku,
      product_sku   : item.product_sku,
      product_name  : item.product_name,
      description   : item.description.trim(),
      currency      : item.currency || NO_CURRENCY_VALUE,
      value         : (item.currency || NO_CURRENCY_VALUE) === NO_CURRENCY_VALUE ? '' : item.value,
      notes         : item.notes.trim()
    }))
    .filter((item) => item.description || item.product_name || item.inventory_id)

  const resetForm = () => {
    setForm({
      ...initialForm,
      receipt_date: defaultDate()
    })
    setItems([ newItem() ])
    setSelectedUser(null)
    setSelectedInventory(null)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    const payloadItems = normalizedItems()

    if (!form.full_name.trim()) {
      pageUtils?.setAlertMsg?.('Full Name is required.', ALERT_TYPES.DANGER)
      return
    }

    if (isDatabaseMode && !form.user_id) {
      pageUtils?.setAlertMsg?.('Choose user from database first.', ALERT_TYPES.DANGER)
      return
    }

    if (!payloadItems.length) {
      pageUtils?.setAlertMsg?.('At least one product/item is required.', ALERT_TYPES.DANGER)
      return
    }

    if (!form.given_by_name.trim()) {
      pageUtils?.setAlertMsg?.('Given by is required.', ALERT_TYPES.DANGER)
      return
    }

    if (!form.received_by_name.trim()) {
      pageUtils?.setAlertMsg?.('Received by is required.', ALERT_TYPES.DANGER)
      return
    }

    if (editId && (!revisionVersion || !revisionNote.trim())) {
      pageUtils?.setAlertMsg?.('Muat data revisi dan isi alasan revisi terlebih dahulu.', ALERT_TYPES.DANGER)
      return
    }
    setSaving(true)

    const saveReceipt = editId ? SalesCashHandoverReceiptsApi.update : SalesCashHandoverReceiptsApi.create
    await saveReceipt({
      id: editId,
      expectedUpdatedAt: revisionVersion,
      revisionNote: revisionNote.trim(),
      payload: {
        ...form,
        currency     : receiptCurrency,
        full_name    : form.full_name.trim(),
        phone_number : form.phone_number.trim(),
        email        : form.email.trim(),
        description  : payloadItems.map((item) => item.description || item.product_name).filter(Boolean).join('\n'),
        value        : totalValue,
        notes        : payloadItems.map((item) => item.notes).filter(Boolean).join('\n'),
        items        : payloadItems
      }
    })
      .then(async (response) => {
        const receipt = response?.data?.cash_handover_receipt

        if (editId) {
          pageUtils?.setAlertMsg?.('Revisi Tanda Terima General tersimpan.', ALERT_TYPES.SUCCESS)
          history.push(`/app/sales/cash-handover-receipts/${receipt.id}`)
          return
        }
        pageUtils?.setAlertMsg?.('Tanda Terima General has been created.', ALERT_TYPES.SUCCESS)
        resetForm()
        setPage(1)
        await fetchReceipts({ nextPage: 1, nextPerPage: perPage, nextQ: q })

        if (receipt?.id) {
          await handleDownloadPdf(receipt.id, receipt.receipt_number)
        }
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to create Tanda Terima General.' })
      })
      .finally(() => setSaving(false))
  }

  const handleSearch = () => {
    setQ(searchInput.trim())
    setPage(1)
  }

  const handleFormKeyDown = (event) => {
    if (event.key !== 'Enter') return

    const tagName = event.target?.tagName?.toString().toLowerCase()
    if (tagName === 'textarea') return

    event.preventDefault()
  }

  const handleDownloadPdf = async (id, receiptNumber) => {
    setDownloadingId(id)

    await SalesCashHandoverReceiptsApi.downloadPdf({ id })
      .then((response) => {
        const blob = new Blob([ response.data ], { type: 'application/pdf' })
        const filename = readFilename(response, `${receiptNumber || 'cash-handover-receipt'}.pdf`)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to download PDF.' })
      })
      .finally(() => setDownloadingId(null))
  }

  const handleOpenDetail = (id) => {
    history.push({ pathname: `/app/sales/cash-handover-receipts/${id}` })
  }

  const handleDeleteReceipt = async (receipt) => {
    if (!window.confirm(`Delete Tanda Terima General ${receipt.receipt_number}?`)) return

    setDeletingId(receipt.id)

    await SalesCashHandoverReceiptsApi.destroy({ id: receipt.id })
      .then((response) => {
        pageUtils?.setAlertMsg?.(response?.data?.message || 'Tanda Terima General has been deleted.', ALERT_TYPES.SUCCESS)
        fetchReceipts({ nextPage: page, nextPerPage: perPage, nextQ: q })
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error.response?.data || { message: 'Failed to delete Tanda Terima General.' })
      })
      .finally(() => setDeletingId(null))
  }

  const handleChangePage = (nextPage) => {
    setPage(nextPage)
  }

  const handleChangePerPage = (nextPerPage) => {
    setPerPage(nextPerPage)
    setPage(1)
  }

  const renderUserSearch = () => (
    <div className='mb-3'>
      <Label>{ 'Search User from Database' }</Label>
      <AsyncSelect
        { ...asyncSelectProps }
        defaultOptions={ false }
        isClearable={ true }
        loadOptions={ loadUserOptions }
        value={ selectedUser }
        onChange={ handleSelectUser }
        placeholder='Search name, email, or phone'
      />
    </div>
  )

  const renderInventorySearch = () => (
    <div className='mb-3'>
      <Label>{ 'Search Product / Inventory from Database' }</Label>
      <AsyncSelect
        { ...asyncSelectProps }
        defaultOptions={ false }
        isClearable={ true }
        loadOptions={ loadInventoryOptions }
        value={ selectedInventory }
        onChange={ handleSelectInventory }
        placeholder='Search inventory SKU / product SKU / product name'
      />
      <small className='text-muted d-block mt-1'>
        { 'Select one product from the dropdown to add it to the receipt.' }
      </small>
    </div>
  )

  const renderItemsTable = () => (
    <Table bordered responsive style={{ tableLayout: 'fixed', minWidth: 950 }}>
      <thead>
        <tr>
          <th style={{ width: '28%' }}>{ 'Product / Description' }</th>
          <th>{ 'Notes' }</th>
          <th style={{ width: 180 }}>{ 'Currency' }</th>
          { showValueColumn ? <th style={{ width: '18%' }}>{ 'Value' }</th> : null }
          <th style={{ width: 80 }} />
        </tr>
      </thead>
      <tbody>
        {
          items.map((item, index) => (
            <tr key={ item.local_key }>
              <td>
                <Input
                  value={ item.description }
                  onChange={ (event) => updateItem(index, 'description', event.target.value) }
                  placeholder='Product / item description'
                />
                {
                  item.inventory_sku || item.product_sku ? (
                    <small className='text-muted d-block mt-1'>
                      { item.inventory_sku || item.product_sku }
                    </small>
                  ) : null
                }
              </td>
              <td>
                <Input
                  value={ item.notes }
                  onChange={ (event) => updateItem(index, 'notes', event.target.value) }
                  placeholder='Additional notes'
                />
              </td>
              <td>
                <Select
                  { ...selectProps }
                  { ...currencySelectProps }
                  options={ CURRENCY_OPTIONS }
                  isClearable={ false }
                  value={ selectedCurrencyOption(item.currency || NO_CURRENCY_VALUE) }
                  onChange={ (option) => handleCurrencyChange(index, option?.value || NO_CURRENCY_VALUE) }
                  placeholder='Type currency keyword'
                />
                <small className='text-muted d-block mt-1'>
                  { 'Default is No Currency. Type a currency keyword first if this item has money/cash value.' }
                </small>
              </td>
              {
                showValueColumn ? (
                  <td>
                    <Input
                      type='number'
                      min='0'
                      value={ (item.currency || NO_CURRENCY_VALUE) === NO_CURRENCY_VALUE ? '' : item.value }
                      onChange={ (event) => updateItem(index, 'value', event.target.value) }
                      placeholder={ (item.currency || NO_CURRENCY_VALUE) === NO_CURRENCY_VALUE ? 'No value needed' : 'Cash amount' }
                      disabled={ (item.currency || NO_CURRENCY_VALUE) === NO_CURRENCY_VALUE }
                    />
                    <small className='text-muted'>{ formatCurrency(item.value, item.currency || NO_CURRENCY_VALUE) }</small>
                  </td>
                ) : null
              }
              <td className='text-right'>
                <Button type='button' color='danger' outline size='sm' onClick={ () => handleRemoveItem(index) }>
                  { 'Remove' }
                </Button>
              </td>
            </tr>
          ))
        }
        {
          showValueColumn ? (
            <tr>
              <td colSpan={ 3 } className='font-weight-bold text-right'>{ 'Total' }</td>
              <td className='font-weight-bold'>{ totalValueLabel }</td>
              <td />
            </tr>
          ) : null
        }
      </tbody>
    </Table>
  )

  return (
    <>
      <TitlePage mainTitle={ editId ? `${PAGE_TITLE} — Edit / Revisi` : PAGE_TITLE } />

      <Container fluid>
        <Row>
          <Col lg='12'>
            <Card>
              <CardBody>
                <Form onSubmit={ handleSubmit } onKeyDown={ handleFormKeyDown }>
                  <Alert color='info'>
                    { 'Create Tanda Terima General from manual input or by searching existing user and product inventory from database. One receipt can contain multiple products/items.' }
                  </Alert>

                  { editId && <div className='mb-3'>
                    <Label>{ 'Alasan revisi (wajib)' }</Label>
                    <Input type='textarea' value={ revisionNote } onChange={ (event) => setRevisionNote(event.target.value) } />
                    <small>{ 'Nomor tanda terima dan pembuat asli tetap tersimpan. Revisi dicatat pada Audit Log.' }</small>
                    <Button type='button' color='secondary' className='ml-2' onClick={ () => history.push(`/app/sales/cash-handover-receipts/${editId}`) }>{ 'Batal revisi' }</Button>
                  </div> }
                  <Row form>
                    <Col md={ 4 }>
                      <Label>{ 'Date' }</Label>
                      <Input
                        type='date'
                        value={ form.receipt_date }
                        onChange={ (event) => updateForm('receipt_date', event.target.value) }
                      />
                    </Col>
                    <Col md={ 4 }>
                      <Label title='Manual = isi customer dan item dengan free text. Search From Database = pilih customer dan product dari database.'>{ 'Input Mode' }</Label>
                      <Input
                        type='select'
                        value={ form.entry_mode }
                        onChange={ (event) => handleEntryModeChange(event.target.value) }
                        title='Manual = isi customer dan item dengan free text. Search From Database = pilih customer dan product dari database.'
                      >
                        {
                          INPUT_MODE_OPTIONS.map((option) => (
                            <option key={ option.value } value={ option.value }>
                              { option.label }
                            </option>
                          ))
                        }
                      </Input>
                      <small className='text-muted d-block mt-1'>
                        { 'Manual = isi customer dan item dengan free text. Search From Database = pilih customer dan product dari database.' }
                      </small>
                    </Col>
                    <Col md={ 4 }>
                      <Label>{ 'Total Value' }</Label>
                      <Input value={ totalValueLabel } disabled />
                    </Col>
                  </Row>

                  {
                    isDatabaseMode ? renderUserSearch() : null
                  }

                  <Row form className='mt-3'>
                    <Col md={ 4 }>
                      <Label>{ 'Full Name' }</Label>
                      <Input
                        value={ form.full_name }
                        onChange={ (event) => updateForm('full_name', event.target.value) }
                        placeholder='Customer full name'
                        disabled={ isDatabaseMode }
                      />
                    </Col>
                    <Col md={ 4 }>
                      <Label>{ 'Phone Number' }</Label>
                      <Input
                        value={ form.phone_number }
                        onChange={ (event) => updateForm('phone_number', event.target.value) }
                        placeholder='Phone number'
                        disabled={ isDatabaseMode }
                      />
                    </Col>
                    <Col md={ 4 }>
                      <Label>{ 'Email (Optional)' }</Label>
                      <Input
                        type='email'
                        value={ form.email }
                        onChange={ (event) => updateForm('email', event.target.value) }
                        placeholder='Email'
                        disabled={ isDatabaseMode }
                      />
                    </Col>
                  </Row>

                  <div className='mt-4'>
                    <h5>{ 'Product / Cash Items' }</h5>
                    {
                      isDatabaseMode ? renderInventorySearch() : (
                        <>
                          <Button type='button' color='primary' outline className='mb-3' onClick={ handleAddManualItem } disabled={ !canAddManualItem }>
                            { 'Add Manual Item' }
                          </Button>
                          {
                            !canAddManualItem ? (
                              <small className='text-muted d-block mb-3'>{ 'Isi item yang masih kosong terlebih dahulu sebelum menambah baris baru.' }</small>
                            ) : null
                          }
                        </>
                      )
                    }
                    { renderItemsTable() }
                  </div>

                  <Row form className='mt-3'>
                    <Col md={ 4 }>
                      <Label>{ 'Given by - name' } <span className='text-danger'>{ '*' }</span></Label>
                      <Input
                        value={ form.given_by_name }
                        onChange={ (event) => updateForm('given_by_name', event.target.value) }
                        placeholder='Given by'
                        required={ true }
                      />
                    </Col>
                    <Col md={ 4 }>
                      <Label>{ 'Received by - name' } <span className='text-danger'>{ '*' }</span></Label>
                      <Input
                        value={ form.received_by_name }
                        onChange={ (event) => updateForm('received_by_name', event.target.value) }
                        placeholder='Received by'
                        required={ true }
                      />
                    </Col>
                    <Col md={ 4 }>
                      <Label>{ 'Received by (2) - name' }</Label>
                      <Input
                        value={ form.second_received_by_name }
                        onChange={ (event) => updateForm('second_received_by_name', event.target.value) }
                        placeholder='Optional second receiver'
                      />
                    </Col>
                  </Row>

                  <div className='d-flex justify-content-end mt-4'>
                    <Button color='success' type='submit' disabled={ saving || loadingRevision || (Boolean(editId) && !revisionVersion) }>
                      {
                        saving ? (
                          <>
                            <Spinner size='sm' className='mr-2' />
                            { 'Creating...' }
                          </>
                        ) : (editId ? 'Simpan Revisi' : 'Create & Download PDF')
                      }
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className='mt-4'>
          <Col lg='12'>
            <Card>
              <CardBody>
                <Row className='align-items-center'>
                  <Col md={ 5 }>
                    <Input
                      value={ searchInput }
                      onChange={ (event) => setSearchInput(event.target.value) }
                      onKeyDown={ (event) => {
                        if (event.key === 'Enter') handleSearch()
                      } }
                      placeholder='Search receipt no, name, phone, email, description...'
                    />
                  </Col>
                  <Col md={ 2 }>
                    <Input type='select' value={ perPage } onChange={ (event) => handleChangePerPage(Number(event.target.value)) }>
                      {
                        PER_PAGE_OPTIONS.map((option) => (
                          <option key={ option } value={ option }>
                            { `${option} / page` }
                          </option>
                        ))
                      }
                    </Input>
                  </Col>
                  <Col md={ 5 } className='text-right'>
                    <Button color='primary' onClick={ handleSearch } disabled={ loading }>
                      { 'Search' }
                    </Button>
                  </Col>
                </Row>

                <div className='mt-3'>
                  {
                    loading ? (
                      <div className='d-flex align-items-center'>
                        <Spinner size='sm' className='mr-2' />
                        { 'Loading...' }
                      </div>
                    ) : (
                      <Table bordered responsive hover>
                        <thead>
                          <tr>
                            <th>{ 'Receipt No' }</th>
                            <th>{ 'Date' }</th>
                            <th>{ 'Full Name' }</th>
                            <th>{ 'Phone' }</th>
                            <th>{ 'Currency' }</th>
                            <th>{ 'Value' }</th>
                            <th>{ 'Created By' }</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {
                            receipts.map((receipt) => (
                              <tr key={ receipt.id }>
                                <td>{ receipt.receipt_number }</td>
                                <td>{ receipt.receipt_date || '-' }</td>
                                <td>{ receipt.full_name || '-' }</td>
                                <td>{ receipt.phone_number || '-' }</td>
                                <td>{ receipt.currency_summary_label || receipt.currency_label || receiptCurrencyLabel(receipt.currency) || '-' }</td>
                                <td>{ receipt.value_label || formatCurrency(receipt.value, receipt.currency) }</td>
                                <td>{ receipt.created_by_name || '-' }</td>
                                <td className='text-right'>
                                  <Button
                                    size='sm'
                                    color='secondary'
                                    outline
                                    className='mr-2'
                                    onClick={ () => handleOpenDetail(receipt.id) }
                                  >
                                    { 'Detail' }
                                  </Button>
                                  <Button
                                    size='sm'
                                    color='primary'
                                    className='mr-2'
                                    onClick={ () => handleDownloadPdf(receipt.id, receipt.receipt_number) }
                                    disabled={ downloadingId === receipt.id }
                                  >
                                    { downloadingId === receipt.id ? 'Preparing...' : 'PDF' }
                                  </Button>
                                  {/* <Button
                                    size='sm'
                                    color='danger'
                                    outline
                                    onClick={ () => handleDeleteReceipt(receipt) }
                                    disabled={ deletingId === receipt.id }
                                  >
                                    { deletingId === receipt.id ? 'Deleting...' : 'Delete' }
                                  </Button> */}
                                </td>
                              </tr>
                            ))
                          }

                          {
                            receipts.length === 0 ? (
                              <tr>
                                <td colSpan={ 8 }>{ 'No Tanda Terima General receipts.' }</td>
                              </tr>
                            ) : null
                          }
                        </tbody>
                      </Table>
                    )
                  }
                </div>

                <div className='d-flex justify-content-between align-items-center mt-3'>
                  <div>
                    { `Showing page ${meta.current_page || 1} of ${meta.total_pages || 1} - Total ${meta.total_count || 0} receipts` }
                  </div>
                  <div>
                    <Button
                      size='sm'
                      disabled={ (meta.current_page || 1) <= 1 }
                      onClick={ () => handleChangePage((meta.current_page || 1) - 1) }
                    >
                      { 'Previous' }
                    </Button>
                    { ' ' }
                    <Button
                      size='sm'
                      disabled={ (meta.current_page || 1) >= (meta.total_pages || 1) }
                      onClick={ () => handleChangePage((meta.current_page || 1) + 1) }
                    >
                      { 'Next' }
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

SalesCashHandoverReceiptsListPage.propTypes = propTypes

export default SalesCashHandoverReceiptsListPage
